import {
  ConditionalFieldData,
  ConditionalFieldWithRules,
  ConditionBooleanOperators,
  ConditionDirectiveKeys,
  ConditionField,
  ConditionFieldType,
  ConditionOperators,
  ConditionSet,
  ConditionsRule
} from './conditionalTypes'
import { truncateString } from '../../utils/stringUtil'
import { IntlShape } from 'react-intl'

export const getDefaultCondition = (params?: { isWorkflow?: boolean }): ConditionField => {
  const isWorkflow = params?.isWorkflow || false

  return {
    fieldId: '',
    operator: isWorkflow ? ConditionOperators.isSet : ConditionOperators.equal,
    values: []
  }
}

export const getDefaultConditionalRule = (params?: { isWorkflow?: boolean }): ConditionsRule => {
  const isWorkflow = params?.isWorkflow || false
  return {
    id: '',
    name: '',
    booleanOperator: ConditionBooleanOperators.and,
    conditionSets: [
      {
        booleanOperator: isWorkflow ? ConditionBooleanOperators.and : ConditionBooleanOperators.or,
        conditions: [getDefaultCondition({ isWorkflow })]
      }
    ],
    directive: { key: ConditionDirectiveKeys.show }
  }
}

export const getValidConditionalRules = (rules: ConditionsRule[] = []) => {
  return rules
    .map((rule) => {
      const conditionSets = (rule.conditionSets || [])
        .map((set) => {
          const conditions = (set.conditions || []).filter(
            (condition) => !!condition.fieldId && !!condition.operator && !!condition.values?.length
          )
          return { ...set, conditions }
        })
        .filter((set) => set.conditions.length > 0)

      return { ...rule, conditionSets }
    })
    .filter((rule) => {
      return (
        rule.id &&
        rule.name &&
        rule.booleanOperator &&
        rule.directive?.key &&
        !!rule.conditionSets?.length
      )
    })
}

const validateCondition = (
  condition: ConditionField,
  values: ConditionalFieldData['values'],
  type?: ConditionFieldType
): boolean => {
  // stringify values to handle cases with negative conditions (value === false)
  const valuesToTest = values.map((val) => val.toString().trim().toLowerCase()) || []

  // validation does not include workflow operators
  return condition.values?.every((val: string | boolean | number) => {
    const conditionVal = val.toString().trim().toLowerCase()

    if (condition.operator === ConditionOperators.equal) {
      // check only 1 value presented in case of multi-select. Check this value equals to test pattern
      return valuesToTest.length === 1 && conditionVal === valuesToTest[0]
    }
    if (condition.operator === ConditionOperators.notEqual) {
      return conditionVal !== valuesToTest[0]
    }
    if (condition.operator === ConditionOperators.contains) {
      // for input/textarea fields check if string contains specified substring
      // dropdown fields check selected options includes specified option
      return type === ConditionFieldType.input
        ? !!valuesToTest[0]?.includes(conditionVal)
        : !!valuesToTest?.includes(conditionVal)
    }

    return false
  })
}

const getConditionDirectiveFieldState = (
  directive: ConditionDirectiveKeys,
  isMatch: boolean
): ConditionDirectiveKeys => {
  if (directive === ConditionDirectiveKeys.show) {
    return isMatch ? ConditionDirectiveKeys.show : ConditionDirectiveKeys.hide
  }
  if (directive === ConditionDirectiveKeys.hide) {
    return isMatch ? ConditionDirectiveKeys.hide : ConditionDirectiveKeys.show
  }
  if (directive === ConditionDirectiveKeys.enable) {
    return isMatch ? ConditionDirectiveKeys.enable : ConditionDirectiveKeys.disable
  }
  if (directive === ConditionDirectiveKeys.disable) {
    return isMatch ? ConditionDirectiveKeys.disable : ConditionDirectiveKeys.enable
  }

  return ConditionDirectiveKeys.show
}

/**
 * test 2nd level, relationship between conditions
 * if set conditions connected between each other with AND operator, test match for all conditions
 * otherwise test condition which contain changed field ID
 */
const testConditions = (set: ConditionSet, fieldsData: ConditionalFieldData[]): boolean => {
  const conditionsToTest = set.conditions
  // test 1 level (basic)
  const conditionMatches: boolean[] = []

  conditionsToTest.forEach((c: ConditionField) => {
    const foundField = fieldsData.find((field) => field.id === c.fieldId)
    conditionMatches.push(validateCondition(c, foundField?.values || [], foundField?.type))
  })

  return set.booleanOperator === ConditionBooleanOperators.and
    ? conditionMatches.every((bool) => !!bool)
    : conditionMatches.some((bool) => !!bool)
}

const removeDeletedConditionsFromRule = (
  fields: ConditionalFieldData[],
  fieldRules: ConditionsRule[]
): ConditionsRule[] => {
  const cleanedRules = [...fieldRules].map((rule) => {
    const conditionSets = rule.conditionSets.map((set) => {
      const conditions = set.conditions.filter(
        (c) => !!fields.find((field) => field.id === c.fieldId)
      )
      return { ...set, conditions }
    })

    return { ...rule, conditionSets: conditionSets.filter((set) => !!set.conditions.length) }
  })

  return cleanedRules.filter((rule) => !!rule.conditionSets.length)
}

/**
 * Manage conditional fields states based on their rules and conditions
 */
export const manageConditions = (
  fieldsData: ConditionalFieldData[],
  fieldRulesData: ConditionalFieldWithRules[]
): ConditionalFieldWithRules[] => {
  // validate conditions and update directive for directive fields
  let updatedFieldRulesData: ConditionalFieldWithRules[] = [...(fieldRulesData || [])]

  type Match = {
    directive: { key: ConditionDirectiveKeys; values?: string[] }
    isMatch: boolean
  }
  fieldRulesData?.forEach((field) => {
    const matches: Match[] = []
    let fieldState: ConditionDirectiveKeys

    const updateMatches = ({ directive, isMatch }) => {
      const foundDirective = matches.find((item) => item.directive.key === directive.key)

      if (!foundDirective) {
        matches.push({ directive, isMatch })
        return
      }
      if (isMatch) foundDirective.isMatch = true
    }

    const cleanedRules = removeDeletedConditionsFromRule(fieldsData, field.rules)
    cleanedRules.forEach((rule) => {
      // if sets connected between each other with AND operator, test all of them which contain changed field ID
      if (rule.booleanOperator === ConditionBooleanOperators.and) {
        const setsMatches = rule.conditionSets.map((set) => testConditions(set, fieldsData))
        const isMatch = setsMatches.every((bool) => !!bool)
        updateMatches({ directive: rule.directive, isMatch })
      } else {
        const setsMatches = rule.conditionSets.map((set) => testConditions(set, fieldsData))
        const isMatch = setsMatches.some((bool) => !!bool)
        updateMatches({ directive: rule.directive, isMatch })
      }
    })
    matches.forEach((match) => {
      fieldState = getConditionDirectiveFieldState(match.directive.key, match.isMatch)
    })

    updatedFieldRulesData = updatedFieldRulesData.map((directiveField) =>
      directiveField.id === field.id ? { ...directiveField, fieldState } : directiveField
    )
  })

  // recursively check visibility of ancestor fields
  const isAncestorsVisible = (field: ConditionalFieldWithRules, maxDepth = 10): boolean => {
    const visited = new Set<string>()

    const checkAncestor = (currentField: ConditionalFieldWithRules, depth: number): boolean => {
      if (!currentField || visited.has(currentField.id) || depth >= maxDepth) {
        return true
      }

      visited.add(currentField.id)

      for (const rule of currentField.rules) {
        for (const set of rule.conditionSets) {
          for (const condition of set.conditions) {
            const ancestor = updatedFieldRulesData.find((f) => f.id === condition.fieldId)
            if (!ancestor) continue

            if (
              ancestor.fieldState === ConditionDirectiveKeys.disable ||
              ancestor.fieldState === ConditionDirectiveKeys.hide
            ) {
              visited.delete(currentField.id)
              return false
            }

            if (ancestor?.rules?.length) {
              if (!checkAncestor(ancestor, depth + 1)) {
                visited.delete(currentField.id)
                return false
              }
            }
          }
        }
      }

      visited.delete(currentField.id)
      return true
    }

    return checkAncestor(field, 0)
  }
  const withUpdatedFieldStates: ConditionalFieldWithRules[] = updatedFieldRulesData.map(
    (field: ConditionalFieldWithRules) => {
      return {
        ...field,
        fieldState: isAncestorsVisible(field) ? field.fieldState : ConditionDirectiveKeys.hide
      }
    }
  )

  return withUpdatedFieldStates
}

export const getConditionalFieldOptionsFormatted = (params: {
  fieldsData?: ConditionalFieldData[]
  intl: IntlShape
  maxTextLength?: number
  selectedField?: string
}) => {
  const { fieldsData = [], selectedField = '', intl, maxTextLength = 25 } = params

  return fieldsData
    ?.filter((field) => field.id !== selectedField)
    ?.map((field) => {
      const separator = ': '
      const sectionName = field?.sectionName
        ? field?.sectionName
        : field.sectionNameKey
        ? intl.formatMessage({ id: field.sectionNameKey })
        : ''

      const fieldName = field.fieldName
        ? field.fieldName
        : field.fieldNameKey
        ? intl.formatMessage({ id: field.fieldNameKey })
        : ''

      const truncatedSectionName = truncateString(sectionName, maxTextLength)
      const truncatedFieldName = truncateString(fieldName, maxTextLength)

      return {
        label: truncatedSectionName + separator + truncatedFieldName,
        value: field.id,
        title: sectionName + separator + fieldName
      }
    })
}
